import type { SystemProps, Theme } from '@mui/system';

// mui 9 removed the system style shorthands from Box, Stack and Typography.
// splitSystemProps pulls them back out of the props and folds them into sx.
export const SYSTEM_PROPS = [
  'mt',
  'mb',
  'mr',
  'p',
  'pt',
  'display',
  'flexDirection',
  'flexWrap',
  'justifyContent',
  'alignItems',
  'alignContent',
  'flex',
  'gap',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'textAlign',
  'textTransform',
  'height',
  'position',
] as const;

export type SystemShorthandProps = Pick<
  SystemProps<Theme>,
  (typeof SYSTEM_PROPS)[number]
>;

// Typography keeps a real color prop, but only for these names. Every other
// value (theme paths like 'text.secondary', hex, rgb) now has to go via sx.
const PALETTE_COLOR_NAMES = new Set([
  'primary',
  'secondary',
  'success',
  'error',
  'info',
  'warning',
  'textPrimary',
  'textSecondary',
  'textDisabled',
]);

/** Returns the remaining props plus an sx array where the caller's sx wins. */
export function splitSystemProps<S>(
  props: object,
  sx: S | undefined,
  options: { paletteColorProp?: boolean } = {}
): { rest: Record<string, unknown>; sx: S } {
  const shorthand: Record<string, unknown> = {};
  const rest = { ...props } as Record<string, unknown>;
  for (const key of SYSTEM_PROPS) {
    if (key in rest) {
      shorthand[key] = rest[key];
      delete rest[key];
    }
  }
  const { color } = rest;
  const keepColor =
    options.paletteColorProp &&
    typeof color === 'string' &&
    PALETTE_COLOR_NAMES.has(color);
  if (color !== undefined && !keepColor) {
    shorthand.color = color;
    delete rest.color;
  }
  return {
    rest,
    sx: [shorthand, ...(Array.isArray(sx) ? sx : [sx])] as S,
  };
}
